import CardHeader from "components/CardHeader";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { Account } from "models/Account";
import { Invoice } from "models/Invoice";
import React, { useMemo } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { RootState } from "store/store";
import { InvoiceState } from "store/reducers/Invoices";
import { useDashboardData } from "./DashboardData";

interface Props {
    invoiceState: InvoiceState;
}

export const finalOpenInvoices = (invoices: Invoice[]) => invoices
    .filter(invoice => !invoice.quote && !invoice.draft && !invoice.IsPaid())
    .sort((left, right) => left.GetDeadlineDate().getTime() - right.GetDeadlineDate().getTime()
        || left.name.localeCompare(right.name));

const accountNames = (accounts: Account[]) => new Map(accounts.map(account => [account.id, account.legal_name || account.id]));

export const OpenInvoicesCardBase: React.FC<Props> = ({ invoiceState }) => {
    const { intlLocale, t } = useOfficeTranslation();
    const { accounts, accountsFailed } = useDashboardData();
    const names = useMemo(() => accountNames(accounts), [accounts]);
    const invoices = useMemo(() => finalOpenInvoices(invoiceState.getInvoices()), [invoiceState]);
    const formatDate = (date: Date) => new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium" }).format(date);
    const formatMoney = (value: number) => new Intl.NumberFormat(intlLocale, { style: "currency", currency: "EUR" }).format(value);
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

    return <Card className="h-100 mb-0 dashboard-widget-card">
        <CardBody>
            <CardHeader
                title={t("dashboard.openInvoicesCard.title")}
                icon="mdi-file-clock-outline"
                count={invoices.length}
                actions={<Link to="/invoices" className="btn btn-sm btn-light" aria-label={t("dashboard.openInvoicesCard.openInvoices")}>
                    <i className="mdi mdi-arrow-right" aria-hidden="true" />
                </Link>}
            />
            {accountsFailed && <p className="text-warning small mt-3 mb-0">{t("dashboard.openInvoicesCard.accountNamesUnavailable")}</p>}
            {invoices.length === 0 && <p className="text-muted mt-3 mb-0">{t("dashboard.openInvoicesCard.empty")}</p>}
            <div className="dashboard-widget-scroll dashboard-operational-list">
                {invoices.map(invoice => {
                    const deadline = invoice.GetDeadlineDate();
                    const overdue = !invoice.IsPayWhenPaid() && deadline.getTime() < startOfToday;
                    return <Link
                        className="dashboard-operational-row text-body text-decoration-none"
                        key={invoice.id}
                        to={`/invoice/${invoice.id}`}
                    >
                        <span className="min-w-0">
                            <strong className="d-block text-truncate">{invoice.GetDisplayName() || t("dashboard.openInvoicesCard.unnamed")}</strong>
                            <small className="text-muted d-block text-truncate">{names.get(invoice.customer) ?? invoice.customer}</small>
                            <small className={overdue ? "text-danger d-block" : "text-muted d-block"}>
                                {invoice.IsPayWhenPaid()
                                    ? t("dashboard.openInvoicesCard.payWhenPaid")
                                    : t(overdue ? "dashboard.openInvoicesCard.overdue" : "dashboard.openInvoicesCard.due", { date: formatDate(deadline) })}
                            </small>
                        </span>
                        <strong className="flex-shrink-0">{formatMoney(invoice.CalcPendingTotal())}</strong>
                    </Link>;
                })}
            </div>
        </CardBody>
    </Card>;
};

const mapStateToProps = (state: RootState) => ({ invoiceState: state.Invoices });

export default connect(mapStateToProps)(OpenInvoicesCardBase);
